import { useState } from "react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Eye, EyeOff, GripVertical, Plus, RotateCcw, Trash2, Link as LinkIcon, Columns2, Rows, LayoutGrid } from "lucide-react";
import { WIDGET_META_HOUSEHOLD, WIDGET_META_BOAT, EDITION_CONFIG } from "@/lib/homeLayout";
import { toast } from "sonner";

const SIZE_LABEL = { small: "Kompakt", medium: "Middels", large: "Stor" };

function SortableRow({ w, onToggleHidden, onSize }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: w.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const meta = WIDGET_META_HOUSEHOLD[w.id] || WIDGET_META_BOAT[w.id] || { label: w.id, description: "" };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 border border-stone-200 rounded-lg bg-white p-2.5 ${w.hidden ? "opacity-60" : ""}`}
      data-testid={`layout-row-${w.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing touch-none"
        title="Dra for å endre rekkefølge"
        aria-label="Flytt"
        data-testid={`layout-drag-${w.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-stone-800">{meta.label}</p>
        <p className="text-[11px] text-stone-500 truncate">{meta.description}</p>
      </div>
      <select
        value={w.size}
        onChange={(e) => onSize(w.id, e.target.value)}
        disabled={w.hidden}
        className="text-xs rounded-md border border-stone-200 bg-white px-2 py-1 focus:outline-none focus:border-amber-500 disabled:opacity-50"
        data-testid={`layout-size-${w.id}`}
      >
        <option value="small">{SIZE_LABEL.small}</option>
        <option value="medium">{SIZE_LABEL.medium}</option>
        <option value="large">{SIZE_LABEL.large}</option>
      </select>
      <button
        onClick={() => onToggleHidden(w.id)}
        className={`p-1.5 rounded-md ${w.hidden ? "text-stone-400 hover:bg-stone-100" : "text-emerald-700 hover:bg-emerald-50"}`}
        title={w.hidden ? "Vis" : "Skjul"}
        data-testid={`layout-toggle-${w.id}`}
      >
        {w.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function HomeLayoutDialog({ open, onOpenChange, layout, onUpdate, onReset, edition = "household" }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [newLabel, setNewLabel] = useState("");
  const [newTarget, setNewTarget] = useState("");

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = layout.widgets.findIndex((w) => w.id === active.id);
    const newIdx = layout.widgets.findIndex((w) => w.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onUpdate({ widgets: arrayMove(layout.widgets, oldIdx, newIdx) });
  };

  const toggleHidden = (id) => {
    onUpdate({
      widgets: layout.widgets.map((w) => (w.id === id ? { ...w, hidden: !w.hidden } : w)),
    });
  };

  const setSize = (id, size) => {
    onUpdate({
      widgets: layout.widgets.map((w) => (w.id === id ? { ...w, size } : w)),
    });
  };

  const addCustomTile = () => {
    const label = newLabel.trim().slice(0, 40);
    let target = newTarget.trim();
    if (!label || !target) { toast.error("Navn og lenke er påkrevd"); return; }
    // Normalize: allow internal paths (start with /) or external http(s)
    if (!target.startsWith("/") && !/^https?:\/\//i.test(target)) {
      target = `https://${target}`;
    }
    if (layout.customTiles.length >= 12) { toast.error("Maks 12 egne snarveier"); return; }
    onUpdate({
      customTiles: [
        ...layout.customTiles,
        { id: `custom-${Date.now()}`, label, icon: "link", target },
      ],
    });
    setNewLabel(""); setNewTarget("");
    toast.success(`«${label}» lagt til`);
  };

  const removeCustomTile = (id) => {
    onUpdate({ customTiles: layout.customTiles.filter((t) => t.id !== id) });
  };

  const handleReset = () => {
    if (!window.confirm("Tilbakestill hele layouten til standard?")) return;
    onReset();
    toast.success("Tilbakestilt");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="home-layout-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Tilpass forsiden</DialogTitle>
          <DialogDescription>
            Skjul, endre størrelse, dra for å sortere, og legg til egne snarveier. Innstillingene lagres på denne enheten.
          </DialogDescription>
        </DialogHeader>

        {/* Global layout controls */}
        <section className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 mt-2">
          <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
            <Rows className="h-4 w-4 text-amber-700" /> Visning
          </h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 block mb-1">Tetthet</label>
              <div className="flex gap-1">
                {["small", "medium", "large"].map((d) => (
                  <button
                    key={d}
                    onClick={() => onUpdate({ density: d })}
                    className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                      layout.density === d
                        ? "bg-amber-600 text-white border-amber-600"
                        : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                    }`}
                    data-testid={`layout-density-${d}`}
                  >
                    {SIZE_LABEL[d]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 block mb-1">Desktop-kolonner</label>
              <div className="flex gap-1">
                <button
                  onClick={() => onUpdate({ columns: 1 })}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                    layout.columns === 1
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                  }`}
                  data-testid="layout-cols-1"
                >
                  <Rows className="h-3 w-3" /> 1 kolonne
                </button>
                <button
                  onClick={() => onUpdate({ columns: 2 })}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                    layout.columns === 2
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                  }`}
                  data-testid="layout-cols-2"
                >
                  <Columns2 className="h-3 w-3" /> 2 kolonner
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Widget list with drag-drop */}
        <section className="mt-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-2 flex items-center gap-2">
            <LayoutGrid className="h-4 w-4 text-amber-700" /> Moduler — dra for å sortere
          </h3>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={layout.widgets.map((w) => w.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5" data-testid="layout-widget-list">
                {layout.widgets.map((w) => (
                  <SortableRow key={w.id} w={w} onToggleHidden={toggleHidden} onSize={setSize} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </section>

        {/* Custom shortcuts */}
        <section className="mt-4 rounded-xl border border-sky-200 bg-sky-50/60 p-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-2 flex items-center gap-2">
            <LinkIcon className="h-4 w-4 text-sky-700" /> Egne snarveier ({layout.customTiles.length}/12)
          </h3>
          <p className="text-[11px] text-stone-600 mb-3">
            Legg til lenker til interne sider (/calendar, /baking) eller eksterne URL-er (nettbank, skole-portal).
          </p>

          {layout.customTiles.length > 0 && (
            <ul className="space-y-1.5 mb-3" data-testid="layout-custom-list">
              {layout.customTiles.map((t) => (
                <li key={t.id} className="flex items-center gap-2 rounded-md bg-white border border-stone-200 px-2.5 py-2" data-testid={`layout-custom-${t.id}`}>
                  <LinkIcon className="h-3.5 w-3.5 text-sky-700 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{t.label}</p>
                    <p className="text-[11px] text-stone-500 truncate">{t.target}</p>
                  </div>
                  <button
                    onClick={() => removeCustomTile(t.id)}
                    className="p-1 rounded-md text-stone-400 hover:text-rose-600 hover:bg-rose-50"
                    data-testid={`layout-custom-remove-${t.id}`}
                    title="Fjern"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-2">
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Navn (f.eks. Nettbank)"
              className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
              data-testid="layout-custom-label"
            />
            <div className="flex gap-2">
              <input
                value={newTarget}
                onChange={(e) => setNewTarget(e.target.value)}
                placeholder="/calendar eller https://…"
                className="flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
                data-testid="layout-custom-target"
              />
              <button
                onClick={addCustomTile}
                disabled={!newLabel.trim() || !newTarget.trim()}
                className="inline-flex items-center gap-1 rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
                data-testid="layout-custom-add"
              >
                <Plus className="h-4 w-4" /> Legg til
              </button>
            </div>
          </div>
        </section>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-4 border-t border-stone-100 mt-4">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-rose-600 font-medium"
            data-testid="layout-reset"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Tilbakestill standard
          </button>
          <button
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1 rounded-md bg-stone-800 px-4 py-2 text-xs font-semibold text-white hover:bg-stone-900"
            data-testid="layout-close"
          >
            Ferdig
          </button>
        </div>

        <p className="text-[10px] text-stone-400 text-center mt-2">
          {((EDITION_CONFIG[edition] || EDITION_CONFIG.household).default.widgets || []).length} standard-moduler · lagres per enhet
        </p>
      </DialogContent>
    </Dialog>
  );
}
